import { CheckIcon, CloseIcon } from "@chakra-ui/icons";
import { chosenValues } from "./chosenValues";
import { ListPageHeader, listHeaderRow } from "../../../common/components/ListPageHeader";
import { Alert, AlertDescription, AlertIcon, Box, Button, CloseButton, Flex, Grid, GridItem, HStack, Icon, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, Tag, Text, VStack } from "@chakra-ui/react";
import { Tooltip } from "../../../common/components/Tooltip";
import { useTranslation } from "react-i18next";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FaEye } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Category,
  ContractDataFieldSectionDto,
  ContractPriority,
  ContractReportDto,
  ContractReportQueryDto,
  ContractReportSortColumn,
  ContractRisk,
  FrameworkRole,
  ContractStateDto,
  SavedReportListDto,
  ContractStatusDto,
  CustomContractDataFieldDto,
  SortDirection,
  useListContractReportsQuery,
} from "../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { useColumnMemory } from "../../../common/entity-table/useColumnMemory";
import { datePresetChoices } from "../../../common/entity-table/datePresetChoices";
import { EntityTable, type EntityTableColumn } from "../../../common/entity-table/EntityTable";
import { useDisplayDate } from "../../../common/formatting/useDisplayDate";
import { displaySum } from "../../../common/formatting/displaySum";
import { SelectorValue } from "../../../common/input/Selector/SelectorValue";
import { TipTap } from "../../../common/input/TipTap/TipTap";
import { useSub } from "../../../common/subscription/useSub";
import { Explanation } from "../../../common/support/Explanation/Explanation";
import { useTranslationTools } from "../../../common/useTranslationTools";
import { hasActiveContractFilters } from "./contractListFilters";
import { withLockedColumnsShown } from "../../../common/entity-table/columnsWithActiveFilter";
import { base64Decode, base64Encode } from "../../../common/utils/base64";
import { CONTRACT_PAGE_SIZE, contractQueryFromAddress, decodedContractQuery } from "./contractQueryFromAddress";
import { urls } from "../../../urls";
import { ProfileImageRounded } from "../../../common/persons/ProfileImageRounded";
import { ContractStatusTag } from "../../../common/contracts/ContractStatusTag";
import { ContractListDownloadMenu } from "./ContractListDownloadMenu";
import { NOT_EXPORTED_COLUMNS } from "./contractListExport";
import { FromProjectLine, fromProjectId } from "./FromProjectLine";
import { overviewFilterChips } from "./OverviewFilterChips";
import { describeColumnFilters } from "../../../common/download/describeFilters";
import { isCustomFieldTextSearchable } from "../../../common/contracts/customFieldFilters";
import { CustomContractFieldValue } from "./CustomContractFieldValue";
import { useContractReportOptions } from "./useContractReportOptions";
import {
  removedCustomFields,
  upToDateContractReportQuery,
} from "./removedCustomFields";
import { FilterBar } from "../../../common/entity-table/FilterBar";
import { type DateField, withDatePreset } from "./contractDatePresets";
import { fieldChoices, withChosenValues } from "./customFieldChoices";
import { useListReports } from "../../../common/saved-reports/useListReports";
import { SearchHighlight } from "../../../common/entity-table/SearchHighlight";
import { ContractSearchNoMatch } from "./ContractSearchNoMatch";
import { DEPARTMENT_FIELD, contractSearchHitOutsideShownColumns } from "./contractSearchHit";


type FiltersState = {
  q: ContractReportQueryDto;
  sortBy?: ContractReportSortColumn;
  sortDirection?: SortDirection;
  sortByCustomField?: string;
};
type FilterUpdates = Omit<FiltersState, "q"> & { q?: Partial<ContractReportQueryDto> };

export const Contracts = () => {
  const { t } = useTranslation();
  const displayDate = useDisplayDate();
  const authState = useLoggedInWithOrgContextState();
  const columnMemory = useColumnMemory("contracts");
  const navigate = useNavigate();
  const location = useLocation();
  const sub = useSub();
  const { getPriorityTranslation, getRiskTranslation } = useTranslationTools();

  const [richTextPreviewContent, setRichTextPreviewContent] = useState<string>();

  const getStateTranslation = (state: string) => {
    return state === ContractStateDto.Published ? t("Published") : t("Draft");
  };

  const getCustomFieldValue = (
    contract: ContractReportDto,
    fieldId: string
  ): CustomContractDataFieldDto | undefined | null => {
    if (!contract.customFields) return null;

    for (const section of Object.values(contract.customFields)) {
      const field = (section as ContractDataFieldSectionDto).fields.find((f) => f.id === fieldId);
      if (field) return field;
    }
    return null;
  };

  const [openedQ] = useState(() => decodedContractQuery(new URLSearchParams(location.search).get("q")));
  const openedFromProjectId = fromProjectId(location.search);
  const [removedFields] = useState(() =>
    removedCustomFields(openedQ, authState.selectedOrg.customContractSections, openedColumnsAndSort(location.search))
  );
  const [removedFieldsNoticeOpen, setRemovedFieldsNoticeOpen] = useState(removedFields.length > 0);

  const [withoutRemovedFields] = useState(() =>
    upToDateContractReportQuery(removedFields, authState.selectedOrg.customContractSections)
  );

  const [filters, setFilters] = useState<FiltersState>(() => {
    const params = new URLSearchParams(withoutRemovedFields(location.search));
    return {
      q: contractQueryFromAddress(params.get("q"), authState.selectedOrg.id),
      sortBy: (params.get("sortBy") as ContractReportSortColumn | null) || undefined,
      sortDirection: (params.get("sortDirection") as SortDirection | null) || undefined,
      sortByCustomField: params.get("sortByCustomField") ?? undefined,
    };
  });

  const [debouncedFilters, setDebouncedFilters] = useState(filters);

  const defaultColumns = useMemo(
    () =>
      new Set([
        "title",
        "counterparty",
        "type",
        "frameworkRole",
        "projectDetails",
        "internalParties",
        "state",
        "status",
        "startDate",
        "endDate",
        "responsible",
        "referenceNumber",
        "owner",
      ]),
    []
  );


  const visibleColumns = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const encodedColumns = params.get("columns");
    if (!encodedColumns) return new Set<string>(columnMemory.recall() ?? defaultColumns);
    const decoded = base64Decode(encodedColumns);
    return new Set(JSON.parse(decoded) as string[]);
  }, [location.search, defaultColumns, columnMemory]);

  const filteredCustomFieldIds = useMemo(() => {
    const q = debouncedFilters.q;
    return new Set([
      ...(q.customFieldSearch ?? []).filter((search) => search.searchTerm?.trim()).map((search) => search.fieldId),
      ...(q.customFieldSelect ?? []).map((select) => select.fieldId),
      ...(q.customFieldValues ?? []).filter((chosen) => chosen.values.length > 0).map((chosen) => chosen.fieldId),
    ]);
  }, [debouncedFilters.q]);

  const visibleCustomFields = useMemo(() => {
    const sections = authState.selectedOrg.customContractSections || {};
    const map: { [key: string]: ContractDataFieldSectionDto } = {};
    for (const section of Object.values(sections)) {
      const visibleFields = section.fields.filter(
        (field) => visibleColumns.has(field.id) || filteredCustomFieldIds.has(field.id)
      );
      if (visibleFields.length > 0) {
        map[section.id] = { id: section.id, name: section.name, fields: visibleFields };
      }
    }
    return map;
  }, [authState.selectedOrg.customContractSections, visibleColumns, filteredCustomFieldIds]);

  const requestQ = useMemo(
    () => ({
      ...debouncedFilters.q,
      visibleCustomFieldIds: Object.values(visibleCustomFields).flatMap((section) =>
        section.fields.map((field) => field.id)
      ),
    }),
    [debouncedFilters.q, visibleCustomFields]
  );
  const encodedQ = useMemo(() => base64Encode(JSON.stringify(requestQ)), [requestQ]);
  const addressQ = useMemo(() => base64Encode(JSON.stringify(debouncedFilters.q)), [debouncedFilters.q]);

  const computeHidden = (key: string): boolean => {
    return !visibleColumns.has(key);
  };

  const getVisibleColumnsFromUrl = useCallback((): string[] => {
    const params = new URLSearchParams(location.search);
    const encoded = params.get("columns");
    if (!encoded) return columnMemory.recall() ?? Array.from(defaultColumns);
    const decoded = base64Decode(encoded);
    return JSON.parse(decoded) as string[];
  }, [columnMemory, defaultColumns, location.search]);

  const setVisibleColumnsToUrl = useCallback(
    (list: string[]): void => {
      columnMemory.remember(list);
      const params = new URLSearchParams(location.search);
      const encoded = base64Encode(JSON.stringify(list));
      params.set("columns", encoded);
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    },
    [columnMemory, location.pathname, location.search, navigate]
  );

  const setDefaultColumnsToUrl = useCallback((): void => {
    columnMemory.forget();
    const params = new URLSearchParams(location.search);
    params.set("columns", base64Encode(JSON.stringify(Array.from(defaultColumns))));
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  }, [columnMemory, defaultColumns, location.pathname, location.search, navigate]);

  const handleShowColumn = useCallback(
    (columnKey: string) => {
      const current = getVisibleColumnsFromUrl();
      const next = current.includes(columnKey) ? current : [...current, columnKey];
      setVisibleColumnsToUrl(next);
    },
    [getVisibleColumnsFromUrl, setVisibleColumnsToUrl]
  );

  const handleHideColumn = useCallback(
    (columnKey: string) => {
      const current = getVisibleColumnsFromUrl();
      const next = current.filter((k) => k !== columnKey);
      setVisibleColumnsToUrl(next);
    },
    [getVisibleColumnsFromUrl, setVisibleColumnsToUrl]
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    Object.entries(debouncedFilters).forEach(([key, value]) => {
      if (key === "q") params.set(key, addressQ);
      else if (value || (typeof value === "number" && value === 0)) params.set(key, `${value}`);
      else params.delete(key);
    });
    navigate(`${location.pathname}?${withoutRemovedFields(params.toString())}`, { replace: true });
  }, [debouncedFilters, navigate, location.pathname, addressQ, location.search, withoutRemovedFields]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleDebouncedFilterChange = useCallback(
    debounce((filter: typeof filters) => {
      setDebouncedFilters(filter);
    }, 300),
    []
  );

  const handleFilterChange = useCallback(
    (updates: FilterUpdates, resetSkip = true) => {
      setFilters((prev) => {
        const newFilter = {
          ...prev,
          ...updates,
          q: {
            ...prev.q,
            ...(updates.q || {}),
            ...(resetSkip && { skip: 0 }),
          },
        };
        handleDebouncedFilterChange(newFilter);
        return newFilter;
      });
    },
    [handleDebouncedFilterChange]
  );

  const handleSearchChange = useCallback(
    (queryKey: keyof ContractReportQueryDto) => (value: string) => {
      handleFilterChange({ q: { [queryKey]: value } });
    },
    [handleFilterChange]
  );

  const handleQueryChange = useCallback(
    (queryKey: keyof ContractReportQueryDto, value?: number | string | SelectorValue[]) => {
      if (Array.isArray(value)) {
        handleFilterChange({ q: { [queryKey]: value.map((v) => v.value) } });
      } else {
        handleFilterChange({ q: { [queryKey]: value === "" ? undefined : value } });
      }
    },
    [handleFilterChange]
  );

  const valueRange = (from: keyof ContractReportQueryDto, to: keyof ContractReportQueryDto) => ({
    isFilterActive: filters.q[from] != null || filters.q[to] != null,
    numberRangeConfig: {
      onFromChange: (value?: number) => handleQueryChange(from, value),
      onToChange: (value?: number) => handleQueryChange(to, value),
      from: filters.q[from] as number | null | undefined,
      to: filters.q[to] as number | null | undefined,
    },
  });


  const presets = datePresetChoices(t);

  const dateRange = (field: DateField) => {
    const fromKey = `${field}From` as const;
    const toKey = `${field}To` as const;
    const withoutPreset = () => withDatePreset(filters.q.datePresets, field, undefined);
    return {
      isFilterActive: !!(filters.q[fromKey] || filters.q[toKey] || filters.q.datePresets?.[field]),
      dateRangeConfig: {
        onFromDateChange: (date?: string) =>
          handleFilterChange({ q: { [fromKey]: date || undefined, datePresets: withoutPreset() } }),
        onToDateChange: (date?: string) =>
          handleFilterChange({ q: { [toKey]: date || undefined, datePresets: withoutPreset() } }),
        fromDate: filters.q[fromKey],
        toDate: filters.q[toKey],
        fromDateLabel: String(t("From")),
        toDateLabel: String(t("To")),
        presets,
        preset: filters.q.datePresets?.[field],
        onPresetChange: (chosen?: string) =>
          handleFilterChange({
            q: {
              [fromKey]: undefined,
              [toKey]: undefined,
              datePresets: withDatePreset(filters.q.datePresets, field, presets.find((choice) => choice.value === chosen)?.value),
            },
          }),
      },
    };
  };

  const choiceFilter = (field: CustomContractDataFieldDto): Partial<EntityTableColumn> => {
    const choices = fieldChoices(field);
    if (!choices) return {};
    const chosen = filters.q.customFieldValues?.find((filter) => filter.fieldId === field.id)?.values ?? [];
    return {
      multiSelectConfig: {
        options: choices.map((choice) => ({ value: choice, label: choice })),
        selectedValues: chosen.map((choice) => ({ value: choice, label: choice })),
        onSelect: (values: SelectorValue[]) =>
          handleFilterChange({
            q: {
              customFieldValues: withChosenValues(
                filters.q.customFieldValues,
                field.id,
                values.map((value) => value.value)
              ),
            },
          }),
      },
    };
  };

  const getCustomFieldSearchTerm = (fieldId: string): string | undefined => {
    const current = filters.q.customFieldSearch || [];
    const match = current.find((c) => c.fieldId === fieldId);
    return match?.searchTerm || undefined;
  };

  const getSelectedCustomBooleanValue = (fieldId: string): SelectorValue | undefined => {
    const booleanSelectionForField = filters.q.customFieldSelect?.find((config) => config.fieldId === fieldId);
    return booleanSelectionForField
      ? {
          value: String(booleanSelectionForField.selected),
          label: booleanSelectionForField.selected ? t("Yes") : t("No"),
        }
      : undefined;
  };

  const handleSelectCustomBoolean =
    ({ fieldId }: { fieldId: string }) =>
    (value: SelectorValue) => {
      const selectedString = typeof value?.value === "string" ? value.value : "";
      const existingCustomFieldSelectFilters = filters.q.customFieldSelect || [];
      const filtersWithoutThisField = existingCustomFieldSelectFilters.filter((config) => config.fieldId !== fieldId);
      const updatedCustomFieldSelectFilters =
        selectedString === ""
          ? filtersWithoutThisField.length > 0
            ? filtersWithoutThisField
            : null
          : [...filtersWithoutThisField, { fieldId, selected: selectedString === "true" }];
      handleFilterChange({ q: { ...filters.q, customFieldSelect: updatedCustomFieldSelectFilters } });
    };

  const handleCustomFieldSearchChange = useCallback(
    (fieldId: string, searchTerm: string) => {
      const prev = filters.q.customFieldSearch || [];
      const without = prev.filter((c) => c.fieldId !== fieldId);
      const next = searchTerm && searchTerm.trim() !== "" ? [...without, { fieldId, searchTerm }] : without;
      handleFilterChange({ q: { ...filters.q, customFieldSearch: next.length > 0 ? next : null } });
    },
    [filters.q, handleFilterChange]
  );

  const createCustomFieldOnSearch = useCallback(
    (fieldId: string) => (value: string) => handleCustomFieldSearchChange(fieldId, value),
    [handleCustomFieldSearchChange]
  );

  const { data: unfiltered } = useListContractReportsQuery(
    {
      q: base64Encode(
        JSON.stringify({ owningOrganizationId: authState.selectedOrg.id, limit: 1, skip: 0 })
      ),
    },
    { skip: !hasActiveContractFilters(filters), refetchOnFocus: false, refetchOnMountOrArgChange: false }
  );
  const { data: lastLoaded, isError, isLoading, isFetching, refetch } = useListContractReportsQuery(
    {
      q: encodedQ,
      sortBy: filters.sortBy,
      sortDirection: filters.sortDirection,
      sortByCustomField: filters.sortByCustomField,
    },
    { refetchOnFocus: false, refetchOnMountOrArgChange: false }
  );
  const data = isError ? undefined : lastLoaded;
  const contractCount = data?.count ?? 0;
  const askedPastTheLastPage = contractCount > 0 && (filters.q.skip ?? 0) >= contractCount;
  const showsPastTheLastPage = contractCount > 0 && (debouncedFilters.q.skip ?? 0) >= contractCount;
  useEffect(() => {
    if (askedPastTheLastPage) {
      handleFilterChange({ q: { skip: Math.floor((contractCount - 1) / CONTRACT_PAGE_SIZE) * CONTRACT_PAGE_SIZE } }, false);
    }
  }, [askedPastTheLastPage, contractCount, handleFilterChange]);
  const listIsLoading = isLoading || isFetching || showsPastTheLastPage;

  const customFields = Object.values(authState.selectedOrg.customContractSections ?? {}).flatMap(
    (section) => section.fields
  );

  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [optionsPages, setOptionsPages] = useState(1);
  const showMoreOptions = useCallback(() => setOptionsPages((pages) => pages + 1), []);

  const [optimisticSelectedValues, setOptimisticSelectedValues] = useState<Record<string, SelectorValue[]>>({});

  const [optionsSearchTerms, setOptionsSearchTerms] = useState<Partial<ContractReportQueryDto>>({});
  const handleOptionsSearchChange = useCallback((key: keyof ContractReportQueryDto, value: string) => {
    setOptionsSearchTerms((prev) => ({ ...prev, [key]: value || undefined }));
  }, []);
  const makeOptionSearchHandler = useCallback(
    (key: keyof ContractReportQueryDto) => (value: string) => handleOptionsSearchChange(key, value),
    [handleOptionsSearchChange]
  );

  const {
    projectOptions,
    projectOptionsLoading,
    projectOptionsFetching,
    projectOptionsFailed,
    refetchProjectOptions,
    selectedProjects,
    selectedProjectsLoading,
    selectedProjectsFetching,
    counterpartyOptions,
    counterpartyOptionsLoading,
    counterpartyOptionsFetching,
    counterpartyOptionsFailed,
    refetchCounterpartyOptions,
    selectedCounterpartyOptions,
    selectedCounterpartyOptionsLoading,
    selectedCounterpartyOptionsFetching,
    internalOwnerOptions,
    internalOwnerOptionsLoading,
    internalOwnerOptionsFetching,
    internalOwnerOptionsFailed,
    refetchInternalOwnerOptions,
    selectedInternalOwnerOptions,
    selectedInternalOwnerOptionsLoading,
    selectedInternalOwnerOptionsFetching,
    owningOrgOptions,
    owningOrgOptionsLoading,
    owningOrgOptionsFetching,
    owningOrgOptionsFailed,
    refetchOwningOrgOptions,
    selectedOwningOrgOptions,
    selectedOwningOrgOptionsLoading,
    selectedOwningOrgOptionsFetching,
    templateOptions,
    templateOptionsLoading,
    templateOptionsFetching,
    templateOptionsFailed,
    refetchTemplateOptions,
    selectedTemplateOptions,
    selectedTemplateOptionsLoading,
    selectedTemplateOptionsFetching,
    referenceNumberOptions,
    referenceNumberOptionsLoading,
    referenceNumberOptionsFetching,
    referenceNumberOptionsFailed,
    refetchReferenceNumberOptions,
    selectedReferenceNumberOptions,
    selectedReferenceNumberOptionsLoading,
    selectedReferenceNumberOptionsFetching,
    internalPartyOptions,
    internalPartyOptionsLoading,
    internalPartyOptionsFetching,
    internalPartyOptionsFailed,
    refetchInternalPartyOptions,
    selectedInternalPartyOptions,
    selectedInternalPartyOptionsLoading,
    selectedInternalPartyOptionsFetching,
    currencyOptions,
    currencyOptionsLoading,
    currencyOptionsFetching,
    currencyOptionsFailed,
    refetchCurrencyOptions,
    originSourcingEventOptions,
    originSourcingEventOptionsLoading,
    originSourcingEventOptionsFetching,
    originSourcingEventOptionsFailed,
    refetchOriginSourcingEventOptions,
    selectedOriginSourcingEventOptions,
    selectedOriginSourcingEventOptionsLoading,
    selectedOriginSourcingEventOptionsFetching,
    publishedByOptions,
    publishedByOptionsLoading,
    publishedByOptionsFetching,
    publishedByOptionsFailed,
    refetchPublishedByOptions,
    selectedPublishedByOptions,
    selectedPublishedByOptionsLoading,
    selectedPublishedByOptionsFetching,
    terminatedByOptions,
    terminatedByOptionsLoading,
    terminatedByOptionsFetching,
    terminatedByOptionsFailed,
    refetchTerminatedByOptions,
    selectedTerminatedByOptions,
    selectedTerminatedByOptionsLoading,
    selectedTerminatedByOptionsFetching,
    counterpartyContactNameOptions,
    counterpartyContactNameOptionsLoading,
    counterpartyContactNameOptionsFetching,
    counterpartyContactNameOptionsFailed,
    refetchCounterpartyContactNameOptions,
    selectedCounterpartyContactNameOptions,
    selectedCounterpartyContactNameOptionsLoading,
    selectedCounterpartyContactNameOptionsFetching,
    counterpartyContactEmailOptions,
    counterpartyContactEmailOptionsLoading,
    counterpartyContactEmailOptionsFetching,
    counterpartyContactEmailOptionsFailed,
    refetchCounterpartyContactEmailOptions,
    selectedCounterpartyContactEmailOptions,
    selectedCounterpartyContactEmailOptionsLoading,
    selectedCounterpartyContactEmailOptionsFetching,
    counterpartyContactPhoneOptions,
    counterpartyContactPhoneOptionsLoading,
    counterpartyContactPhoneOptionsFetching,
    counterpartyContactPhoneOptionsFailed,
    refetchCounterpartyContactPhoneOptions,
    selectedCounterpartyContactPhoneOptions,
    selectedCounterpartyContactPhoneOptionsLoading,
    selectedCounterpartyContactPhoneOptionsFetching,
    linkedContractOptions,
    linkedContractOptionsLoading,
    linkedContractOptionsFetching,
    linkedContractOptionsFailed,
    refetchLinkedContractOptions,
    selectedLinkedContractOptions,
    selectedLinkedContractOptionsLoading,
    selectedLinkedContractOptionsFetching,
  } = useContractReportOptions({ debouncedFilters, openDropdown, optionsSearchTerms, optionsPages });

  const handleContractClick = (contract: ContractReportDto) => {
    if (contract.state === ContractStateDto.Published) navigate(urls.contracts.viewSingle.go(contract.contractId));
    else if (contract.state === ContractStateDto.Draft) navigate(urls.contracts.edit.go(contract.contractId).details);
    else throw new Error("Invalid contract status");
  };

  const handleSort = useCallback(
    (column: ContractReportSortColumn) => (direction: SortDirection) => {
      setFilters((prev) => {
        const newFilter = {
          ...prev,
          sortBy: direction ? column : undefined,
          sortByCustomField: undefined,
          sortDirection: direction,
          q: { ...prev.q, skip: 0 },
        };
        handleDebouncedFilterChange(newFilter);
        return newFilter;
      });
    },
    [handleDebouncedFilterChange]
  );

  const handleCustomFieldSort = useCallback(
    (fieldId: string) => (direction: SortDirection) => {
      setFilters((prev) => {
        const newFilter = {
          ...prev,
          sortBy: undefined,
          sortByCustomField: direction ? fieldId : undefined,
          sortDirection: direction,
          q: { ...prev.q, skip: 0 },
        };
        handleDebouncedFilterChange(newFilter);
        return newFilter;
      });
    },
    [handleDebouncedFilterChange]
  );

  const handleDropdownOpen = useCallback((dropdownKey: string) => {
    setOpenDropdown(dropdownKey);
    setOptionsPages(1);
  }, []);

  const handleDropdownClose = useCallback(
    (dropdownKey: string) => setOpenDropdown((open) => (open === dropdownKey ? null : open)),
    [],
  );

  // Fixed select options
  const stateOptions: SelectorValue[] = Object.values(ContractStateDto).map((state) => ({
    value: state,
    label: getStateTranslation(state),
  }));

  const statusOptions: SelectorValue[] = Object.values(ContractStatusDto).map((status) => ({
    value: status,
    label: t(status),
  }));

  const priorityOptions: SelectorValue[] = Object.values(ContractPriority).map((priority) => ({
    value: priority,
    label: getPriorityTranslation(priority).props.children,
  }));

  const riskOptions: SelectorValue[] = Object.values(ContractRisk).map((risk) => ({
    value: risk,
    label: getRiskTranslation(risk).props.children,
  }));

  const frameworkRoleOptions: SelectorValue[] = Object.values(FrameworkRole).map((role) => ({
    value: role,
    label: t(role),
  }));

  const supplierCategoryOptions: SelectorValue[] = Object.values(Category).map((supplierCategory) => ({
    value: supplierCategory,
    label: supplierCategory,
  }));

  const unfoundWhile = useCallback(
    (loading: boolean) =>
      (_value: string, unfoundCount: number): string =>
        loading ? "…" : String(t("{{count}} chosen", { count: unfoundCount })),
    [t],
  );

  // Convert string filters to SelectorValue arrays for multi-select components
  const selectedValues: { [key in keyof Partial<ContractReportQueryDto>]: SelectorValue[] } = useMemo(() => {
    const baseValues = {
      statuses: (filters.q.statuses
        ? filters.q.statuses
            .filter((status): status is ContractStatusDto => Boolean(status))
            .map((status) => ({
              value: status,
              label: statusOptions.find((option) => option.value === status)?.label || t(String(status)),
            }))
        : []) as SelectorValue[],
      priorityLevels: (filters.q.priorityLevels
        ? filters.q.priorityLevels.map((value) => ({
            value,
            label: priorityOptions.find((option) => option.value === value)?.label || value,
          }))
        : []) as SelectorValue[],
      riskLevels: (filters.q.riskLevels
        ? filters.q.riskLevels.map((value) => ({
            value,
            label: riskOptions.find((option) => option.value === value)?.label || value,
          }))
        : []) as SelectorValue[],
      frameworkRoles: (filters.q.frameworkRoles
        ? filters.q.frameworkRoles.map((value) => ({ value, label: t(value) }))
        : []) as SelectorValue[],
      supplierCategories: (filters.q.supplierCategories
        ? filters.q.supplierCategories.map((value) => ({
            value,
            label: supplierCategoryOptions.find((option) => option.value === value)?.label || value,
          }))
        : []) as SelectorValue[],
      projectIds: chosenValues(
        filters.q.projectIds,
        selectedProjects?.projects?.map((project) => ({ value: project.id, label: project.name })),
        unfoundWhile(selectedProjectsLoading || selectedProjectsFetching),
      ),
      counterpartyIds: chosenValues(
        filters.q.counterpartyIds,
        selectedCounterpartyOptions?.options,
        unfoundWhile(selectedCounterpartyOptionsLoading || selectedCounterpartyOptionsFetching),
      ),
      internalOwnerIds: chosenValues(
        filters.q.internalOwnerIds,
        selectedInternalOwnerOptions?.options,
        unfoundWhile(selectedInternalOwnerOptionsLoading || selectedInternalOwnerOptionsFetching),
      ),
      internalPartyIds: chosenValues(
        filters.q.internalPartyIds,
        selectedInternalPartyOptions?.options,
        unfoundWhile(selectedInternalPartyOptionsLoading || selectedInternalPartyOptionsFetching),
      ),
      templateIds: chosenValues(
        filters.q.templateIds,
        selectedTemplateOptions?.options,
        unfoundWhile(selectedTemplateOptionsLoading || selectedTemplateOptionsFetching),
      ),
      owningOrganizationIds: chosenValues(
        filters.q.owningOrganizationIds,
        selectedOwningOrgOptions?.options,
        unfoundWhile(selectedOwningOrgOptionsLoading || selectedOwningOrgOptionsFetching),
      ),
      referenceNumbers: chosenValues(
        filters.q.referenceNumbers,
        selectedReferenceNumberOptions?.options,
        (value) => value,
      ),
      currencies:
        filters.q.currencies?.map((value) => ({
          value,
          label: value,
        })) ?? [],
      originSourcingEventIds: chosenValues(
        filters.q.originSourcingEventIds,
        selectedOriginSourcingEventOptions?.options,
        unfoundWhile(selectedOriginSourcingEventOptionsLoading || selectedOriginSourcingEventOptionsFetching),
      ),
      publishedByIds: chosenValues(
        filters.q.publishedByIds,
        selectedPublishedByOptions?.options,
        unfoundWhile(selectedPublishedByOptionsLoading || selectedPublishedByOptionsFetching),
      ),
      terminatedByIds: chosenValues(
        filters.q.terminatedByIds,
        selectedTerminatedByOptions?.options,
        unfoundWhile(selectedTerminatedByOptionsLoading || selectedTerminatedByOptionsFetching),
      ),
      counterpartyContactFullNames: chosenValues(
        filters.q.counterpartyContactFullNames,
        selectedCounterpartyContactNameOptions?.options,
        (value) => value,
      ),
      counterpartyContactEmails: chosenValues(
        filters.q.counterpartyContactEmails,
        selectedCounterpartyContactEmailOptions?.options,
        (value) => value,
      ),
      counterpartyContactPhoneNumbers: chosenValues(
        filters.q.counterpartyContactPhoneNumbers,
        selectedCounterpartyContactPhoneOptions?.options,
        (value) => value,
      ),
      linkedContractIds: chosenValues(
        filters.q.linkedContractIds,
        selectedLinkedContractOptions?.options,
        unfoundWhile(selectedLinkedContractOptionsLoading || selectedLinkedContractOptionsFetching),
      ),
    };

    return {
      ...baseValues,
      ...optimisticSelectedValues,
    };
  }, [
    filters.q.priorityLevels,
    filters.q.riskLevels,
    filters.q.frameworkRoles,
    filters.q.supplierCategories,
    filters.q.statuses,
    filters.q.projectIds,
    filters.q.counterpartyIds,
    filters.q.internalOwnerIds,
    filters.q.internalPartyIds,
    filters.q.templateIds,
    filters.q.owningOrganizationIds,
    filters.q.referenceNumbers,
    filters.q.currencies,
    filters.q.originSourcingEventIds,
    filters.q.publishedByIds,
    filters.q.terminatedByIds,
    filters.q.counterpartyContactFullNames,
    filters.q.counterpartyContactEmails,
    filters.q.counterpartyContactPhoneNumbers,
    filters.q.linkedContractIds,
    selectedProjects?.projects,
    selectedCounterpartyOptions?.options,
    selectedInternalOwnerOptions?.options,
    selectedInternalPartyOptions?.options,
    selectedTemplateOptions?.options,
    selectedOwningOrgOptions?.options,
    selectedReferenceNumberOptions?.options,
    selectedOriginSourcingEventOptions?.options,
    selectedPublishedByOptions?.options,
    selectedTerminatedByOptions?.options,
    selectedCounterpartyContactNameOptions?.options,
    selectedCounterpartyContactEmailOptions?.options,
    selectedCounterpartyContactPhoneOptions?.options,
    selectedLinkedContractOptions?.options,
    selectedProjectsLoading,
    selectedProjectsFetching,
    selectedCounterpartyOptionsLoading,
    selectedCounterpartyOptionsFetching,
    selectedInternalOwnerOptionsLoading,
    selectedInternalOwnerOptionsFetching,
    selectedInternalPartyOptionsLoading,
    selectedInternalPartyOptionsFetching,
    selectedTemplateOptionsLoading,
    selectedTemplateOptionsFetching,
    selectedOwningOrgOptionsLoading,
    selectedOwningOrgOptionsFetching,
    selectedOriginSourcingEventOptionsLoading,
    selectedOriginSourcingEventOptionsFetching,
    selectedPublishedByOptionsLoading,
    selectedPublishedByOptionsFetching,
    selectedTerminatedByOptionsLoading,
    selectedTerminatedByOptionsFetching,
    selectedLinkedContractOptionsLoading,
    selectedLinkedContractOptionsFetching,
    optimisticSelectedValues,
    unfoundWhile,
    priorityOptions,
    riskOptions,
    supplierCategoryOptions,
    statusOptions,
  ]);

  // Optimistic select handler to prevent flashing during updates
  const handleOptimisticSelect = useCallback(
    (queryKey: keyof typeof selectedValues, values: SelectorValue[]) => {
      setOptimisticSelectedValues((prev) => ({
        ...prev,
        [queryKey]: values,
      }));

      handleQueryChange(queryKey, values);
    },
    [handleQueryChange]
  );

  const handleResetFilters = useCallback(
    (filterInput?: {
      q?: Partial<ContractReportQueryDto>;
      sortBy?: ContractReportSortColumn;
      sortDirection?: SortDirection;
      sortByCustomField?: string;
    }) => {
      const newFilters = {
        ...filterInput,
        q: {
          limit: 10,
          skip: 0,
          ...filterInput?.q,
          owningOrganizationId: authState.selectedOrg.id,
        },
      };
      setFilters(newFilters);
      setDebouncedFilters(newFilters);
      setOpenDropdown(null);
      setOptimisticSelectedValues({});
      setOptionsSearchTerms({});
    },
    [authState.selectedOrg.id]
  );

  const handleReset = useCallback(() => {
    setDefaultColumnsToUrl();
    handleResetFilters();
  }, [setDefaultColumnsToUrl, handleResetFilters]);

  const handleClearOverviewFilter = useCallback(
    (keys: (keyof ContractReportQueryDto)[]) => {
      const q: Partial<ContractReportQueryDto> = { ...filters.q, skip: 0 };
      keys.forEach((key) => delete q[key]);
      handleResetFilters({
        q,
        sortBy: filters.sortBy,
        sortDirection: filters.sortDirection,
        sortByCustomField: filters.sortByCustomField,
      });
    },
    [filters, handleResetFilters]
  );

  const hasActiveFilters = useMemo(() => hasActiveContractFilters(filters), [filters]);

  const hasColumnChanges = useMemo((): boolean => {
    if (visibleColumns.size !== defaultColumns.size) return true;
    for (const key of visibleColumns) {
      if (!defaultColumns.has(key)) return true;
    }
    for (const key of defaultColumns) {
      if (!visibleColumns.has(key)) return true;
    }
    return false;
  }, [visibleColumns, defaultColumns]);

  const sortOn = (column: ContractReportSortColumn): Partial<EntityTableColumn> => ({
    onSort: handleSort(column),
    currentSortDirection: filters.sortBy === column ? filters.sortDirection : undefined,
  });

  const unlockedColumns: EntityTableColumn[] = [
    {
      key: "title",
      label: t("Title"),
      width: "240px",
      hidden: computeHidden("title"),
      alwaysShown: true,
      onSort: handleSort(ContractReportSortColumn.Title),
      currentSortDirection:
        filters.sortBy === ContractReportSortColumn.Title ? filters.sortDirection : undefined,
      onSearch: (value: string) => handleSearchChange("titleFilter")(value),
      currentSearchValue: filters.q.titleFilter ?? undefined,
    },
    {
      key: "counterparty",
      label: t("Counterparty"),
      width: "240px",
      hidden: computeHidden("counterparty"),
      isFilterActive: (filters.q.counterpartyIds?.length ?? 0) > 0,
      onSort: handleSort(ContractReportSortColumn.CounterpartyName),
      currentSortDirection:
        filters.sortBy === ContractReportSortColumn.CounterpartyName ? filters.sortDirection : undefined,
      onSearch: (value: string) => handleSearchChange("counterpartyName")(value),
      currentSearchValue: filters.q.counterpartyName ?? undefined,
      multiSelectConfig: {
        options: counterpartyOptions?.options || [],
        onSelect: (values: SelectorValue[]) => handleOptimisticSelect("counterpartyIds", values),
        selectedValues: selectedValues.counterpartyIds,
        optionsCount: counterpartyOptions?.count,
        onLoadMore: showMoreOptions,
        optionsLimit: 10,
        isLoading: counterpartyOptionsLoading || counterpartyOptionsFetching,
        isError: counterpartyOptionsFailed,
        onRetry: refetchCounterpartyOptions,
        selectedValuesLoading: selectedCounterpartyOptionsLoading || selectedCounterpartyOptionsFetching,
        onInputChange: makeOptionSearchHandler("counterpartyName"),
      },
    },
    {
      key: "type",
      label: t("Type"),
      width: "180px",
      hidden: computeHidden("type"),
      isFilterActive: (filters.q.templateIds?.length ?? 0) > 0,
      onSort: handleSort(ContractReportSortColumn.TemplateName),
      currentSortDirection:
        filters.sortBy === ContractReportSortColumn.TemplateName ? filters.sortDirection : undefined,
      onSearch: (value: string) => handleSearchChange("templateNameSearchTerm")(value),
      currentSearchValue: filters.q.templateNameSearchTerm ?? undefined,
      multiSelectConfig: {
        options: templateOptions?.options || [],
        onSelect: (values: SelectorValue[]) => handleOptimisticSelect("templateIds", values),
        selectedValues: selectedValues.templateIds,
        optionsCount: templateOptions?.count,
        onLoadMore: showMoreOptions,
        optionsLimit: 10,
        isLoading: templateOptionsLoading || templateOptionsFetching,
        isError: templateOptionsFailed,
        onRetry: refetchTemplateOptions,
        selectedValuesLoading: selectedTemplateOptionsLoading || selectedTemplateOptionsFetching,
        onInputChange: makeOptionSearchHandler("templateNameSearchTerm"),
      },
    },
    {
      key: "frameworkRole",
      label: t("Role"),
      width: "180px",
      hidden: computeHidden("frameworkRole"),
      ...sortOn(ContractReportSortColumn.FrameworkRole),
      isFilterActive: (filters.q.frameworkRoles?.length ?? 0) > 0,
      multiSelectConfig: {
        options: frameworkRoleOptions,
        onSelect: (values: SelectorValue[]) => handleOptimisticSelect("frameworkRoles", values),
        selectedValues: selectedValues.frameworkRoles,
      },
    },
    {
      key: "projectDetails",
      label: t("Projects"),
      width: "240px",
      hidden: sub.hasProjectAccess ? computeHidden("projectDetails") : true,
      isFilterActive: (filters.q.projectIds?.length ?? 0) > 0,
      onSearch: (value: string) => handleSearchChange("projectSearchTerm")(value),
      currentSearchValue: filters.q.projectSearchTerm ?? undefined,
      multiSelectConfig: {
        options: projectOptions?.options || [],
        onSelect: (values: SelectorValue[]) => handleOptimisticSelect("projectIds", values),
        selectedValues: selectedValues.projectIds,
        optionsCount: projectOptions?.count,
        onLoadMore: showMoreOptions,
        optionsLimit: 10,
        isLoading: projectOptionsLoading || projectOptionsFetching,
        isError: projectOptionsFailed,
        onRetry: refetchProjectOptions,
        selectedValuesLoading: selectedProjectsLoading || selectedProjectsFetching,
        onInputChange: makeOptionSearchHandler("projectSearchTerm"),
      },
    },
    {
      key: "internalParties",
      label: t("Internal parties"),
      width: "200px",
      hidden: computeHidden("internalParties"),
      isFilterActive: (filters.q.internalPartyIds?.length ?? 0) > 0,
      onSearch: (value: string) => handleSearchChange("internalPartySearchTerm")(value),
      currentSearchValue: filters.q.internalPartySearchTerm ?? undefined,
      multiSelectConfig: {
        options: internalPartyOptions?.options || [],
        onSelect: (values: SelectorValue[]) => handleOptimisticSelect("internalPartyIds", values),
        selectedValues: selectedValues.internalPartyIds,
        optionsCount: internalPartyOptions?.count,
        onLoadMore: showMoreOptions,
        optionsLimit: 10,
        isLoading: internalPartyOptionsLoading || internalPartyOptionsFetching,
        isError: internalPartyOptionsFailed,
        onRetry: refetchInternalPartyOptions,
        selectedValuesLoading: selectedInternalPartyOptionsLoading || selectedInternalPartyOptionsFetching,
        onInputChange: makeOptionSearchHandler("internalPartySearchTerm"),
      },
    },
    {
      key: "state",
      label: t("State"),
      width: "160px",
      hidden: computeHidden("state"),
      isFilterActive: !!filters.q.state,
      onSort: handleSort(ContractReportSortColumn.State),
      currentSortDirection:
        filters.sortBy === ContractReportSortColumn.State ? filters.sortDirection : undefined,
      singleSelectConfig: {
        options: stateOptions,
        onSelect: (value: SelectorValue) =>
          handleFilterChange({ q: { ...filters.q, state: value.value ? value.value : undefined } }),
        selectedValue: filters.q.state ? { label: getStateTranslation(filters.q.state), value: filters.q.state } : undefined,
      },
    },
    {
      key: "status",
      label: t("Status"),
      width: "160px",
      hidden: computeHidden("status"),
      ...sortOn(ContractReportSortColumn.Status),
      isFilterActive: (filters.q.statuses?.length ?? 0) > 0,
      multiSelectConfig: {
        options: statusOptions,
        onSelect: (values: SelectorValue[]) => handleOptimisticSelect("statuses", values),
        selectedValues: selectedValues.statuses,
      },
    },
    {
      key: "startDate",
      label: t("Start date"),
      width: "160px",
      hidden: computeHidden("startDate"),
      ...dateRange("startDate"),
      onSort: handleSort(ContractReportSortColumn.StartDate),
      currentSortDirection:
        filters.sortBy === ContractReportSortColumn.StartDate ? filters.sortDirection : undefined,
    },
    {
      key: "endDate",
      label: t("End date"),
      width: "180px",
      hidden: computeHidden("endDate"),
      ...dateRange("endDate"),
      onSort: handleSort(ContractReportSortColumn.EndDate),
      currentSortDirection:
        filters.sortBy === ContractReportSortColumn.EndDate ? filters.sortDirection : undefined,
    },
    {
      key: "hasNoEndDate",
      label: t("No end date"),
      width: "180px",
      hidden: computeHidden("hasNoEndDate"),
      ...sortOn(ContractReportSortColumn.HasNoEndDate),
      isFilterActive: typeof filters.q.hasNoEndDate === "boolean",
      singleSelectConfig: {
        options: [
          { value: "true", label: t("Yes") },
          { value: "false", label: t("No") },
        ],
        onSelect: (value) => {
          const next = value.value === "" ? undefined : value.value === "true";
          handleFilterChange({ q: { ...filters.q, hasNoEndDate: next as boolean | undefined } });
        },
        selectedValue:
          typeof filters.q.hasNoEndDate === "boolean"
            ? { value: String(filters.q.hasNoEndDate), label: filters.q.hasNoEndDate ? t("Yes") : t("No") }
            : undefined,
      },
    },
    {
      key: "responsible",
      label: t("Responsible"),
      width: "200px",
      hidden: computeHidden("responsible"),
      isFilterActive: (filters.q.internalOwnerIds?.length ?? 0) > 0,
      onSort: handleSort(ContractReportSortColumn.InternalOwnerName),
      onSearch: (value: string) => handleSearchChange("internalOwnerSearchTerm")(value),
      currentSearchValue: filters.q.internalOwnerSearchTerm ?? undefined,
      currentSortDirection:
        filters.sortBy === ContractReportSortColumn.InternalOwnerName ? filters.sortDirection : undefined,
      multiSelectConfig: {
        options: internalOwnerOptions?.options || [],
        onSelect: (values: SelectorValue[]) => handleOptimisticSelect("internalOwnerIds", values),
        selectedValues: selectedValues.internalOwnerIds,
        optionsCount: internalOwnerOptions?.count,
        onLoadMore: showMoreOptions,
        optionsLimit: 10,
        isLoading: internalOwnerOptionsLoading || internalOwnerOptionsFetching,
        isError: internalOwnerOptionsFailed,
        onRetry: refetchInternalOwnerOptions,
        selectedValuesLoading: selectedInternalOwnerOptionsLoading || selectedInternalOwnerOptionsFetching,
        onInputChange: makeOptionSearchHandler("internalOwnerSearchTerm"),
      },
    },
    {
      key: "estimatedValue",
      label: t("Estimated value"),
      width: "160px",
      hidden: computeHidden("estimatedValue"),
      ...valueRange("estimatedValueFrom", "estimatedValueTo"),
      onSort: handleSort(ContractReportSortColumn.EstimatedValue),
      currentSortDirection:
        filters.sortBy === ContractReportSortColumn.EstimatedValue ? filters.sortDirection : undefined,
    },
    {
      key: "totalPrice",
      label: t("Total price"),
      width: "160px",
      hidden: computeHidden("totalPrice"),
      ...valueRange("totalPriceFrom", "totalPriceTo"),
      onSort: handleSort(ContractReportSortColumn.TotalPrice),
      currentSortDirection:
        filters.sortBy === ContractReportSortColumn.TotalPrice ? filters.sortDirection : undefined,
    },
    {
      key: "priority",
      label: t("Priority"),
      width: "180px",
      hidden: computeHidden("priority"),
      isFilterActive: (filters.q.priorityLevels?.length ?? 0) > 0,
      onSort: handleSort(ContractReportSortColumn.Priority),
      currentSortDirection:
        filters.sortBy === ContractReportSortColumn.Priority ? filters.sortDirection : undefined,
      multiSelectConfig: {
        options: priorityOptions,
        onSelect: (values: SelectorValue[]) => handleOptimisticSelect("priorityLevels", values),
        selectedValues: selectedValues.priorityLevels,
      },
    },
    {
      key: "risk",
      label: t("Risk"),
      width: "160px",
      hidden: computeHidden("risk"),
      isFilterActive: (filters.q.riskLevels?.length ?? 0) > 0,
      onSort: handleSort(ContractReportSortColumn.Risk),
      currentSortDirection:
        filters.sortBy === ContractReportSortColumn.Risk ? filters.sortDirection : undefined,
      multiSelectConfig: {
        options: riskOptions,
        onSelect: (values: SelectorValue[]) => handleOptimisticSelect("riskLevels", values),
        selectedValues: selectedValues.riskLevels,
      },
    },
    {
      key: "referenceNumber",
      label: t("Reference"),
      width: "200px",
      hidden: computeHidden("referenceNumber"),
      isFilterActive: (filters.q.referenceNumbers?.length ?? 0) > 0,
      onSort: handleSort(ContractReportSortColumn.ReferenceNumber),
      currentSortDirection:
        filters.sortBy === ContractReportSortColumn.ReferenceNumber ? filters.sortDirection : undefined,
      onSearch: (value: string) => handleSearchChange("referenceNumberSearchTerm")(value),
      currentSearchValue: filters.q.referenceNumberSearchTerm ?? undefined,
      isTextColumn: true,
      multiSelectConfig: {
        options: referenceNumberOptions?.options || [],
        onSelect: (values: SelectorValue[]) => handleOptimisticSelect("referenceNumbers", values),
        selectedValues: selectedValues.referenceNumbers,
        optionsCount: referenceNumberOptions?.count,
        onLoadMore: showMoreOptions,
        isLoading: referenceNumberOptionsLoading || referenceNumberOptionsFetching,
        isError: referenceNumberOptionsFailed,
        onRetry: refetchReferenceNumberOptions,
        selectedValuesLoading:
          selectedReferenceNumberOptionsLoading || selectedReferenceNumberOptionsFetching,
      },
    },
    {
      key: "createdAt",
      label: t("Created at"),
      width: "160px",
      hidden: computeHidden("createdAt"),
      ...dateRange("createdAt"),
      onSort: handleSort(ContractReportSortColumn.CreatedAt),
      currentSortDirection:
        filters.sortBy === ContractReportSortColumn.CreatedAt ? filters.sortDirection : undefined,
    },
    {
      key: "modifiedAt",
      label: t("Modified at"),
      width: "160px",
      hidden: computeHidden("modifiedAt"),
      ...dateRange("modifiedAt"),
      onSort: handleSort(ContractReportSortColumn.ModifiedAt),
      currentSortDirection:
        filters.sortBy === ContractReportSortColumn.ModifiedAt ? filters.sortDirection : undefined,
    },
    {
      key: "publishedAt",
      label: t("Published at"),
      width: "160px",
      hidden: computeHidden("publishedAt"),
      ...dateRange("publishedAt"),
      onSort: handleSort(ContractReportSortColumn.PublishedAt),
      currentSortDirection:
        filters.sortBy === ContractReportSortColumn.PublishedAt ? filters.sortDirection : undefined,
    },
    {
      key: "version",
      label: t("Version"),
      width: "160px",
      hidden: computeHidden("version"),
      ...sortOn(ContractReportSortColumn.Version),
    },
    {
      key: "description",
      label: t("Description"),
      width: "200px",
      hidden: computeHidden("description"),
      ...sortOn(ContractReportSortColumn.Description),
      onSearch: (value: string) => handleSearchChange("descriptionSearchTerm")(value),
      currentSearchValue: filters.q.descriptionSearchTerm ?? undefined,
    },
    {
      key: "timezone",
      label: t("Timezone"),
      width: "160px",
      hidden: computeHidden("timezone"),
      ...sortOn(ContractReportSortColumn.Timezone),
    },
    {
      key: "currency",
      label: t("Currency"),
      width: "160px",
      hidden: computeHidden("currency"),
      isFilterActive: (filters.q.currencies?.length ?? 0) > 0,
      onSort: handleSort(ContractReportSortColumn.Currency),
      currentSortDirection:
        filters.sortBy === ContractReportSortColumn.Currency ? filters.sortDirection : undefined,
      multiSelectConfig: {
        options: currencyOptions?.options || [],
        onSelect: (values: SelectorValue[]) => handleOptimisticSelect("currencies", values),
        selectedValues: selectedValues.currencies,
        optionsCount: currencyOptions?.count,
        onLoadMore: showMoreOptions,
        isLoading: currencyOptionsLoading || currencyOptionsFetching,
        isError: currencyOptionsFailed,
        onRetry: refetchCurrencyOptions,
      },
    },
    {
      key: "createdBy",
      label: t("Created by"),
      width: "160px",
      hidden: computeHidden("createdBy"),
      ...sortOn(ContractReportSortColumn.CreatedByName),
    },
    {
      key: "renewalDate",
      label: t("Renewal date"),
      width: "160px",
      hidden: computeHidden("renewalDate"),
      ...dateRange("renewalDate"),
      onSort: handleSort(ContractReportSortColumn.RenewalDate),
      currentSortDirection:
        filters.sortBy === ContractReportSortColumn.RenewalDate ? filters.sortDirection : undefined,
    },
    {
      key: "originSourcingEvent",
      label: t("Origin sourcing event"),
      width: "240px",
      hidden: computeHidden("originSourcingEvent"),
      isFilterActive: (filters.q.originSourcingEventIds?.length ?? 0) > 0,
      onSort: handleSort(ContractReportSortColumn.OriginSourcingEventName),
      currentSortDirection:
        filters.sortBy === ContractReportSortColumn.OriginSourcingEventName
          ? filters.sortDirection
          : undefined,
      onSearch: (value: string) => handleSearchChange("originSourcingEventName")(value),
      currentSearchValue: filters.q.originSourcingEventName ?? undefined,
      multiSelectConfig: {
        options: originSourcingEventOptions?.options || [],
        onSelect: (values: SelectorValue[]) => handleOptimisticSelect("originSourcingEventIds", values),
        selectedValues: selectedValues.originSourcingEventIds,
        optionsCount: originSourcingEventOptions?.count,
        onLoadMore: showMoreOptions,
        isLoading: originSourcingEventOptionsLoading || originSourcingEventOptionsFetching,
        isError: originSourcingEventOptionsFailed,
        onRetry: refetchOriginSourcingEventOptions,
        selectedValuesLoading:
          selectedOriginSourcingEventOptionsLoading || selectedOriginSourcingEventOptionsFetching,
        onInputChange: makeOptionSearchHandler("originSourcingEventName"),
      },
    },
    {
      key: "publishedBy",
      label: t("Published by"),
      width: "160px",
      hidden: computeHidden("publishedBy"),
      isFilterActive: (filters.q.publishedByIds?.length ?? 0) > 0,
      onSort: handleSort(ContractReportSortColumn.PublishedByName),
      currentSortDirection:
        filters.sortBy === ContractReportSortColumn.PublishedByName ? filters.sortDirection : undefined,
      onSearch: (value: string) => handleSearchChange("publishedBySearchTerm")(value),
      currentSearchValue: filters.q.publishedBySearchTerm ?? undefined,
      multiSelectConfig: {
        options: publishedByOptions?.options || [],
        onSelect: (values: SelectorValue[]) => handleOptimisticSelect("publishedByIds", values),
        selectedValues: selectedValues.publishedByIds,
        optionsCount: publishedByOptions?.count,
        onLoadMore: showMoreOptions,
        isLoading: publishedByOptionsLoading || publishedByOptionsFetching,
        isError: publishedByOptionsFailed,
        onRetry: refetchPublishedByOptions,
        selectedValuesLoading: selectedPublishedByOptionsLoading || selectedPublishedByOptionsFetching,
      },
    },
    {
      key: "noticePeriod",
      label: t("Notice period"),
      width: "160px",
      hidden: computeHidden("noticePeriod"),
      ...sortOn(ContractReportSortColumn.NoticePeriod),
    },
    {
      key: "documentCount",
      label: t("Documents"),
      width: "160px",
      hidden: computeHidden("documentCount"),
      ...sortOn(ContractReportSortColumn.DocumentCount),
    },
    {
      key: "signedDocumentCount",
      label: t("Signed docs"),
      width: "160px",
      hidden: computeHidden("signedDocumentCount"),
      ...sortOn(ContractReportSortColumn.SignedDocumentCount),
    },
    {
      key: "terminationDate",
      label: t("Termination date"),
      width: "240px",
      hidden: computeHidden("terminationDate"),
      ...sortOn(ContractReportSortColumn.TerminationDate),
      ...dateRange("terminationDate"),
    },
    {
      key: "terminatedBy",
      label: t("Terminated by"),
      width: "240px",
      hidden: computeHidden("terminatedBy"),
      isFilterActive: (filters.q.terminatedByIds?.length ?? 0) > 0,
      onSort: handleSort(ContractReportSortColumn.TerminatedByName),
      currentSortDirection:
        filters.sortBy === ContractReportSortColumn.TerminatedByName ? filters.sortDirection : undefined,
      onSearch: (value: string) => handleSearchChange("terminatedBySearchTerm")(value),
      currentSearchValue: filters.q.terminatedBySearchTerm ?? undefined,
      multiSelectConfig: {
        options: terminatedByOptions?.options || [],
        onSelect: (values: SelectorValue[]) => handleOptimisticSelect("terminatedByIds", values),
        selectedValues: selectedValues.terminatedByIds,
        optionsCount: terminatedByOptions?.count,
        onLoadMore: showMoreOptions,
        isLoading: terminatedByOptionsLoading || terminatedByOptionsFetching,
        isError: terminatedByOptionsFailed,
        onRetry: refetchTerminatedByOptions,
        selectedValuesLoading: selectedTerminatedByOptionsLoading || selectedTerminatedByOptionsFetching,
      },
    },
    {
      key: "terminationReason",
      label: t("Termination reason"),
      width: "180px",
      hidden: computeHidden("terminationReason"),
      onSort: handleSort(ContractReportSortColumn.TerminationReason),
      currentSortDirection:
        filters.sortBy === ContractReportSortColumn.TerminationReason ? filters.sortDirection : undefined,
    },
    {
      key: "counterpartyContactFullName",
      label: t("Counterparty contact name"),
      width: "240px",
      hidden: computeHidden("counterpartyContactFullName"),
      isFilterActive: (filters.q.counterpartyContactFullNames?.length ?? 0) > 0,
      onSort: handleSort(ContractReportSortColumn.CounterpartyContactFullName),
      currentSortDirection:
        filters.sortBy === ContractReportSortColumn.CounterpartyContactFullName
          ? filters.sortDirection
          : undefined,
      onSearch: (value: string) => handleSearchChange("counterpartyContactFullName")(value),
      currentSearchValue: filters.q.counterpartyContactFullName ?? undefined,
      multiSelectConfig: {
        options: counterpartyContactNameOptions?.options || [],
        onSelect: (values: SelectorValue[]) =>
          handleOptimisticSelect("counterpartyContactFullNames", values),
        selectedValues: selectedValues.counterpartyContactFullNames,
        optionsCount: counterpartyContactNameOptions?.count,
        onLoadMore: showMoreOptions,
        isLoading: counterpartyContactNameOptionsLoading || counterpartyContactNameOptionsFetching,
        isError: counterpartyContactNameOptionsFailed,
        onRetry: refetchCounterpartyContactNameOptions,
        selectedValuesLoading:
          selectedCounterpartyContactNameOptionsLoading || selectedCounterpartyContactNameOptionsFetching,
      },
    },
    {
      key: "counterpartyContactEmail",
      label: t("Counterparty contact email"),
      width: "240px",
      hidden: computeHidden("counterpartyContactEmail"),
      isFilterActive: (filters.q.counterpartyContactEmails?.length ?? 0) > 0,
      onSort: handleSort(ContractReportSortColumn.CounterpartyContactEmail),
      currentSortDirection:
        filters.sortBy === ContractReportSortColumn.CounterpartyContactEmail
          ? filters.sortDirection
          : undefined,
      onSearch: (value: string) => handleSearchChange("counterpartyContactEmail")(value),
      currentSearchValue: filters.q.counterpartyContactEmail ?? undefined,
      multiSelectConfig: {
        options: counterpartyContactEmailOptions?.options || [],
        onSelect: (values: SelectorValue[]) => handleOptimisticSelect("counterpartyContactEmails", values),
        selectedValues: selectedValues.counterpartyContactEmails,
        optionsCount: counterpartyContactEmailOptions?.count,
        onLoadMore: showMoreOptions,
        isLoading: counterpartyContactEmailOptionsLoading || counterpartyContactEmailOptionsFetching,
        isError: counterpartyContactEmailOptionsFailed,
        onRetry: refetchCounterpartyContactEmailOptions,
        selectedValuesLoading:
          selectedCounterpartyContactEmailOptionsLoading || selectedCounterpartyContactEmailOptionsFetching,
      },
    },
    {
      key: "counterpartyContactPhone",
      label: t("Counterparty contact phone"),
      width: "240px",
      hidden: computeHidden("counterpartyContactPhone"),
      isFilterActive: (filters.q.counterpartyContactPhoneNumbers?.length ?? 0) > 0,
      onSort: handleSort(ContractReportSortColumn.CounterpartyContactPhoneNumber),
      currentSortDirection:
        filters.sortBy === ContractReportSortColumn.CounterpartyContactPhoneNumber
          ? filters.sortDirection
          : undefined,
      onSearch: (value: string) => handleSearchChange("counterpartyContactPhoneNumber")(value),
      currentSearchValue: filters.q.counterpartyContactPhoneNumber ?? undefined,
      multiSelectConfig: {
        options: counterpartyContactPhoneOptions?.options || [],
        onSelect: (values: SelectorValue[]) =>
          handleOptimisticSelect("counterpartyContactPhoneNumbers", values),
        selectedValues: selectedValues.counterpartyContactPhoneNumbers,
        optionsCount: counterpartyContactPhoneOptions?.count,
        onLoadMore: showMoreOptions,
        isLoading: counterpartyContactPhoneOptionsLoading || counterpartyContactPhoneOptionsFetching,
        isError: counterpartyContactPhoneOptionsFailed,
        onRetry: refetchCounterpartyContactPhoneOptions,
        selectedValuesLoading:
          selectedCounterpartyContactPhoneOptionsLoading || selectedCounterpartyContactPhoneOptionsFetching,
      },
    },
    {
      key: "externalLink",
      label: t("External link"),
      width: "160px",
      hidden: computeHidden("externalLink"),
      ...sortOn(ContractReportSortColumn.ExternalLink),
    },
    {
      key: "linkedContracts",
      label: t("Linked contracts"),
      width: "160px",
      hidden: computeHidden("linkedContracts"),
      isFilterActive: (filters.q.linkedContractIds?.length ?? 0) > 0,
      onSearch: (value: string) => handleSearchChange("linkedContractTitleSearchTerm")(value),
      currentSearchValue: filters.q.linkedContractTitleSearchTerm ?? undefined,
      multiSelectConfig: {
        options: linkedContractOptions?.options || [],
        onSelect: (values: SelectorValue[]) => handleOptimisticSelect("linkedContractIds", values),
        selectedValues: selectedValues.linkedContractIds,
        optionsCount: linkedContractOptions?.count,
        onLoadMore: showMoreOptions,
        isLoading: linkedContractOptionsLoading || linkedContractOptionsFetching,
        isError: linkedContractOptionsFailed,
        onRetry: refetchLinkedContractOptions,
        selectedValuesLoading:
          selectedLinkedContractOptionsLoading || selectedLinkedContractOptionsFetching,
      },
    },
    {
      key: "paymentTerms",
      label: t("Payment terms"),
      width: "160px",
      hidden: computeHidden("paymentTerms"),
      ...sortOn(ContractReportSortColumn.PaymentTermsInDays),
    },
    {
      key: "pricePerMonth",
      label: t("Price per month"),
      width: "160px",
      hidden: computeHidden("pricePerMonth"),
      ...sortOn(ContractReportSortColumn.PricePerMonth),
      ...valueRange("pricePerMonthFrom", "pricePerMonthTo"),
    },
    {
      key: "pricePerYear",
      label: t("Price per year"),
      width: "160px",
      hidden: computeHidden("pricePerYear"),
      ...sortOn(ContractReportSortColumn.PricePerYear),
      ...valueRange("pricePerYearFrom", "pricePerYearTo"),
    },
    {
      key: "annualValue",
      label: t("Annual value"),
      width: "160px",
      hidden: computeHidden("annualValue"),
      ...sortOn(ContractReportSortColumn.AnnualValue),
    },
    {
      key: "purchasingPolicy",
      label: t("Purchasing policy"),
      width: "160px",
      hidden: computeHidden("purchasingPolicy"),
      ...sortOn(ContractReportSortColumn.PurchasingPolicy),
    },
    {
      key: "supplierInfo",
      label: t("Supplier info"),
      width: "160px",
      hidden: computeHidden("supplierInfo"),
    },
    {
      key: "totalLiability",
      label: t("Total liability"),
      width: "160px",
      hidden: computeHidden("totalLiability"),
      ...sortOn(ContractReportSortColumn.TotalLiability),
      ...valueRange("totalLiabilityFrom", "totalLiabilityTo"),
    },
    {
      key: "warrantyExpiration",
      label: t("Warranty expiration"),
      width: "160px",
      hidden: computeHidden("warrantyExpiration"),
      ...sortOn(ContractReportSortColumn.WarrantyExpirationDate),
    },
    {
      key: "supplierCategory",
      label: t("Supplier category"),
      width: "260px",
      hidden: computeHidden("supplierCategory"),
      isFilterActive: (filters.q.supplierCategories?.length ?? 0) > 0,
      onSort: handleSort(ContractReportSortColumn.SupplierCategory),
      currentSortDirection:
        filters.sortBy === ContractReportSortColumn.SupplierCategory ? filters.sortDirection : undefined,
      multiSelectConfig: {
        options: supplierCategoryOptions,
        onSelect: (values: SelectorValue[]) => handleOptimisticSelect("supplierCategories", values),
        selectedValues: selectedValues.supplierCategories,
        optionsCount: supplierCategoryOptions.length,
      },
    },
    {
      key: "options",
      label: t("Contract options"),
      width: "160px",
      hidden: computeHidden("options"),
      ...sortOn(ContractReportSortColumn.OptionCount),
    },
    {
      key: "owner",
      label: t("Owner"),
      width: "180px",
      hidden: computeHidden("owner"),
      isFilterActive: (filters.q.owningOrganizationIds?.length ?? 0) > 0,
      onSort: handleSort(ContractReportSortColumn.OwningOrganizationName),
      currentSortDirection:
        filters.sortBy === ContractReportSortColumn.OwningOrganizationName
          ? filters.sortDirection
          : undefined,
      onSearch: (value: string) => handleSearchChange("owningOrganizationName")(value),
      currentSearchValue: filters.q.owningOrganizationName ?? undefined,
      multiSelectConfig: {
        options: owningOrgOptions?.options || [],
        onSelect: (values: SelectorValue[]) => handleOptimisticSelect("owningOrganizationIds", values),
        selectedValues: selectedValues.owningOrganizationIds,
        optionsCount: owningOrgOptions?.count,
        onLoadMore: showMoreOptions,
        optionsLimit: 10,
        isLoading: owningOrgOptionsLoading || owningOrgOptionsFetching,
        isError: owningOrgOptionsFailed,
        onRetry: refetchOwningOrgOptions,
        selectedValuesLoading: selectedOwningOrgOptionsLoading || selectedOwningOrgOptionsFetching,
      },
    },
    ...customFields.map((field) => ({
      key: field.id,
      label: field.name,
      width: "160px",
      hidden: computeHidden(field.id),
      isCustomField: true,
      ...(field.type !== "CustomRichText" &&
        field.type !== "CustomMultiEnum" && {
          onSort: handleCustomFieldSort(field.id),
          currentSortDirection: filters.sortByCustomField === field.id ? filters.sortDirection : undefined,
        }),
      ...(isCustomFieldTextSearchable(field.type) && {
        onSearch: createCustomFieldOnSearch(field.id),
        currentSearchValue: getCustomFieldSearchTerm(field.id),
      }),
      ...choiceFilter(field),
      ...(field.type === "CustomBoolean" && {
        singleSelectConfig: {
          options: [
            { value: "true", label: t("Yes") },
            { value: "false", label: t("No") },
          ],
          onSelect: handleSelectCustomBoolean({ fieldId: field.id }),
          selectedValue: getSelectedCustomBooleanValue(field.id),
        },
      }),
    })),
    {
      key: "actions",
      label: t("Actions"),
      width: "160px",
    },
  ];
  const columns = withLockedColumnsShown(unlockedColumns);

  const overviewChips = overviewFilterChips(t, filters.q).map((chip) => ({
    label: chip.label,
    onRemove: () => handleClearOverviewFilter(chip.keys),
  }));
  const searchedTerm = debouncedFilters.q.tableSearchTerm?.trim() ?? "";
  const countText =
    data?.count == null || unfiltered?.count == null
      ? null
      : searchedTerm
        ? t("{{shown}} of {{count}} contracts with «{{term}}»", {
            shown: data.count,
            count: unfiltered.count,
            term: searchedTerm,
          })
        : t("{{shown}} of {{count}} contracts", { shown: data.count, count: unfiltered.count });
  const tableSearch = {
    value: filters.q.tableSearchTerm ?? "",
    onChange: (value: string) => handleFilterChange({ q: { tableSearchTerm: value.trim() ? value : undefined } }),
    label: String(t("Search the table")),
    tooltip: String(t("Searches Title, Counterparty, Reference, Projects, Responsible and Department")),
  };
  const activeFilterDescriptions = [
    ...overviewChips.map((chip) => chip.label),
    ...describeColumnFilters(t, columns),
  ];
  const shownColumnKeys = new Set(columns.filter((column) => !column.hidden).map((column) => column.key));
  const searchedFieldLabel = (field: string) =>
    field === DEPARTMENT_FIELD ? t("Department") : (columns.find((column) => column.key === field)?.label ?? field);
  const handleResetAllFilters = () =>
    handleResetFilters({
      sortBy: filters.sortBy,
      sortDirection: filters.sortDirection,
      sortByCustomField: filters.sortByCustomField,
    });

  const exportColumns = columns
    .filter((column) => !column.hidden && !NOT_EXPORTED_COLUMNS.has(column.key))
    .map(({ key, label }) => ({ key, label: String(label) }));

  const reports = useListReports({
    list: SavedReportListDto.Contracts,
    query: location.search,
    heading: t("Contracts"),
    listFailedToLoad: isError,
    listIsLoading,
    comparable: withoutRemovedFields,
  });

  return (
    <>
      <Modal isOpen={!!richTextPreviewContent} onClose={() => setRichTextPreviewContent(undefined)} size="3xl">
        <ModalOverlay />
        <ModalContent maxH="80vh">
          <ModalHeader>{t("Preview")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <TipTap content={richTextPreviewContent || ""} editable={false} hasBorder={true} />
          </ModalBody>
        </ModalContent>
      </Modal>
      <Grid
        height="full"
        gridTemplateColumns="minmax(0, 1fr)"
        gridTemplateRows="1fr"
      >
        <GridItem px="2">
          <ListPageHeader
            heading={reports.heading}
            {...listHeaderRow}
            data-testid="contract-list-header"
            actions={
              <>
                {reports.actions}
                <ContractListDownloadMenu
                  encodedQ={encodedQ}
                  contractList={{
                    count: data?.count,
                    isLoading: isLoading || isFetching,
                    failed: isError,
                    filters: [
                      ...overviewChips.map((chip) => chip.label),
                      ...describeColumnFilters(t, columns),
                    ],
                    columns: exportColumns,
                    query: {
                      q: requestQ,
                      sortBy: filters.sortBy,
                      sortDirection: filters.sortDirection,
                      sortByCustomField: filters.sortByCustomField,
                    },
                  }}
                />
              </>
            }
          />
          {reports.notice}
          <Box maxWidth="full" overflow="hidden" pt="5" px="2">
            {removedFieldsNoticeOpen && (
              <Alert status="warning" rounded="md" mb="4" data-testid="removed-custom-fields">
                <AlertIcon />
                <AlertDescription flex="1">
                  {removedFields.every((field) => field.name !== null)
                    ? t("{{count}} columns were left out because their custom field no longer exists: {{names}}", {
                        count: removedFields.length,
                        names: removedFields.map((field) => `«${field.name}»`).join(", "),
                      })
                    : t("{{count}} filters or columns were left out because their custom field no longer exists", {
                        count: removedFields.length,
                      })}
                </AlertDescription>
                <CloseButton
                  size="sm"
                  aria-label={String(t("Close"))}
                  onClick={() => setRemovedFieldsNoticeOpen(false)}
                />
              </Alert>
            )}
            {openedFromProjectId && (
              <FromProjectLine projectId={openedFromProjectId} query={filters.q} onClear={handleClearOverviewFilter} />
            )}
            <FilterBar
              columns={columns}
              extraChips={overviewChips}
              onResetAll={handleResetAllFilters}
              countText={countText}
              debounceMs={500}
              search={tableSearch}
              onDropdownOpen={handleDropdownOpen}
              onDropdownClose={handleDropdownClose}
            />
            <EntityTable
              emptyText={t("No contracts yet.")}
              noMatchText={t("No contracts match your search or filters.")}
              noMatchContent={
                searchedTerm ? (
                  <ContractSearchNoMatch
                    term={searchedTerm}
                    activeFilters={activeFilterDescriptions}
                    onClearSearch={() => handleFilterChange({ q: { tableSearchTerm: undefined } })}
                    onResetFilters={handleResetAllFilters}
                  />
                ) : undefined
              }
              loadError={isError ? { message: t("Could not load contracts."), onRetry: () => void refetch() } : undefined}
              onDropdownOpen={handleDropdownOpen}
              onDropdownClose={handleDropdownClose}
              onShowColumn={handleShowColumn}
              onHideColumn={handleHideColumn}
              onShowAllColumns={setVisibleColumnsToUrl}
              onResetColumns={setDefaultColumnsToUrl}
              debounceMs={500}
              hasColumnChanges={hasColumnChanges}
              columns={columns}
              rows={
                data?.reports?.map((contract) => {
                  const hiddenHit = contractSearchHitOutsideShownColumns(contract, searchedTerm, shownColumnKeys);
                  return [
                  <Box key="title">
                    <Tooltip label={contract.title}>
                      <Button
                        variant="unstyled"
                        textAlign="left"
                        width="full"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                        onClick={() => handleContractClick(contract)}
                      >
                        <SearchHighlight text={contract.title} term={searchedTerm} />
                      </Button>
                    </Tooltip>
                    {hiddenHit && (
                      <Text fontSize="xs" color="smMuted" noOfLines={1} data-testid="hidden-search-hit">
                        {t("Match in {{column}}:", { column: searchedFieldLabel(hiddenHit.field) })}{" "}
                        <SearchHighlight text={hiddenHit.value} term={searchedTerm} />
                      </Text>
                    )}
                  </Box>,
                  contract.counterpartyName ? (
                    <Flex key="counterparty" align="center">
                      <Box>
                        <ProfileImageRounded
                          name={contract.counterpartyName}
                          backgroundColor="smSecondary"
                          width="5"
                          fontSize="xx-small"
                        />
                      </Box>
                      <Text fontSize="sm" pl="1.5" overflow="hidden" textOverflow="ellipsis">
                        <SearchHighlight text={contract.counterpartyName} term={searchedTerm} />
                      </Text>
                    </Flex>
                  ) : (
                    <Text key="counterparty" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  // Type (Template Name)
                  contract.templateName ? (
                    <Text key="type" fontSize="sm" overflow="hidden" textOverflow="ellipsis">
                      {contract.templateName}
                    </Text>
                  ) : (
                    <Text key="type" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  contract.frameworkAgreementId && contract.frameworkAgreementMayOpen ? (
                    <Text
                      key="frameworkRole"
                      as="button"
                      textAlign="left"
                      fontSize="sm"
                      color="smPrimary"
                      onClick={() =>
                        navigate(urls.app.frameworkAgreements.view.go(contract.frameworkAgreementId ?? ""))
                      }
                    >
                      {t(contract.frameworkRole)}
                    </Text>
                  ) : (
                    <Text key="frameworkRole" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t(contract.frameworkRole)}
                    </Text>
                  ),
                  contract.projectDetails?.length ? (
                    <Flex key="projectDetails" flexDirection="column" maxWidth="full">
                      {contract.projectDetails.map((p, index) => (
                        <Tag
                          key={index}
                          size="sm"
                          width="full"
                          maxWidth="full"
                          mb="1"
                          cursor={authState.selectedOrg.canViewProjects ? "pointer" : undefined}
                          onClick={() =>
                            authState.selectedOrg.canViewProjects && navigate(urls.projects.view.go(p.projectId))
                          }
                        >
                          <Tooltip label={p.projectName} rounded="md" size="sm">
                            <Text overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" width="full">
                              <SearchHighlight text={p.projectName} term={searchedTerm} />
                            </Text>
                          </Tooltip>
                        </Tag>
                      ))}
                    </Flex>
                  ) : (
                    <Text key="projectDetails" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("None")}
                    </Text>
                  ),
                  <Text key="internalParties" fontSize="sm" overflow="hidden" textOverflow="ellipsis">
                    {contract.internalParties?.length ? (
                      contract.internalParties.map((p) => p.organizationName).join(", ")
                    ) : (
                      <Text as="i" color="smMuted">
                        {t("Not provided")}
                      </Text>
                    )}
                  </Text>,
                  contract.state ? (
                    <Tag key="state" size="sm" colorScheme="blue" variant="outline">
                      {getStateTranslation(contract.state)}
                    </Tag>
                  ) : (
                    <Tag key="state" size="sm" colorScheme="gray" variant="outline">
                      {t("Draft")}
                    </Tag>
                  ),
                  contract.status ? (
                    <Flex key="status">
                      <ContractStatusTag status={contract.status} />
                    </Flex>
                  ) : null,
                  contract.startDate ? (
                    <Text key="startDate" fontSize="sm">
                      {displayDate(contract.startDate)}
                    </Text>
                  ) : (
                    <Text key="startDate" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  contract.endDate ? (
                    <Text key="endDate" fontSize="sm">
                      {displayDate(contract.endDate)}
                    </Text>
                  ) : (
                    <Text key="endDate" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  <Text key="hasNoEndDate" fontSize="sm">
                    {contract.endDate ? t("No") : t("Yes")}
                  </Text>,
                  contract.internalOwnerName ? (
                    <Flex key="responsible" align="center">
                      <Box>
                        <ProfileImageRounded
                          name={contract.internalOwnerName}
                          backgroundColor="smPrimary"
                          width="20px"
                          fontSize="xx-small"
                        />
                      </Box>
                      <Text fontSize="sm" pl="1.5" overflow="hidden" textOverflow="ellipsis">
                        <SearchHighlight text={contract.internalOwnerName} term={searchedTerm} />
                      </Text>
                    </Flex>
                  ) : (
                    <Text key="responsible" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  contract.estimatedValue ? (
                    <Text key="estimatedValue" fontSize="sm">
                      {displaySum({ value: contract.estimatedValue, currency: contract.currency ?? "NOK" })}
                    </Text>
                  ) : (
                    <Text key="estimatedValue" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  contract.totalPrice ? (
                    <Text key="totalPrice" fontSize="sm">
                      {displaySum({ value: contract.totalPrice, currency: contract.currency ?? "NOK" })}
                    </Text>
                  ) : (
                    <Text key="totalPrice" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  contract.priority ? (
                    <Tag key="priority" size="sm" colorScheme="orange" variant="outline">
                      {getPriorityTranslation(contract.priority as ContractPriority)}
                    </Tag>
                  ) : (
                    <Text key="priority" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not set")}
                    </Text>
                  ),
                  contract.risk ? (
                    <Tag key="risk" size="sm" colorScheme="red" variant="outline">
                      {getRiskTranslation(contract.risk as ContractRisk)}
                    </Tag>
                  ) : (
                    <Text key="risk" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not set")}
                    </Text>
                  ),
                  contract.referenceNumber ? (
                    <Text key="referenceNumber" fontSize="sm">
                      <SearchHighlight text={contract.referenceNumber} term={searchedTerm} />
                    </Text>
                  ) : (
                    <Text key="referenceNumber" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  contract.createdAt ? (
                    <Text key="createdAt" fontSize="sm">
                      {displayDate(contract.createdAt)}
                    </Text>
                  ) : (
                    <Text key="createdAt" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  contract.modifiedAt ? (
                    <Text key="modifiedAt" fontSize="sm">
                      {displayDate(contract.modifiedAt)}
                    </Text>
                  ) : (
                    <Text key="modifiedAt" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  contract.publishedAt ? (
                    <Text key="publishedAt" fontSize="sm">
                      {displayDate(contract.publishedAt)}
                    </Text>
                  ) : (
                    <Text key="publishedAt" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not published")}
                    </Text>
                  ),
                  <Text key="version" fontSize="sm">
                    {contract.version}
                  </Text>,
                  contract.description ? (
                    <Tooltip key="description" label={contract.description}>
                      <Text fontSize="sm" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                        {contract.description}
                      </Text>
                    </Tooltip>
                  ) : (
                    <Text key="description" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  contract.timezone ? (
                    <Text key="timezone" fontSize="sm">
                      {contract.timezone}
                    </Text>
                  ) : (
                    <Text key="timezone" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  contract.currency ? (
                    <Text key="currency" fontSize="sm">
                      {contract.currency}
                    </Text>
                  ) : (
                    <Text key="currency" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  contract.createdByName ? (
                    <Flex key="createdBy" align="center">
                      <Box>
                        <ProfileImageRounded
                          name={contract.createdByName}
                          backgroundColor="smPrimary"
                          width="20px"
                          fontSize="xx-small"
                        />
                      </Box>
                      <Text fontSize="sm" pl="1.5" overflow="hidden" textOverflow="ellipsis">
                        {contract.createdByName}
                      </Text>
                    </Flex>
                  ) : (
                    <Text key="createdBy" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  contract.renewalDate ? (
                    <Text key="renewalDate" fontSize="sm">
                      {displayDate(contract.renewalDate)}
                    </Text>
                  ) : (
                    <Text key="renewalDate" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  contract.originSourcingEventName ? (
                    <Text key="originSourcingEvent" fontSize="sm" overflow="hidden" textOverflow="ellipsis">
                      {contract.originSourcingEventName}
                    </Text>
                  ) : (
                    <Text key="originSourcingEvent" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  contract.publishedByName ? (
                    <Flex key="publishedBy" align="center">
                      <Box>
                        <ProfileImageRounded
                          name={contract.publishedByName}
                          backgroundColor="smPrimary"
                          width="20px"
                          fontSize="xx-small"
                        />
                      </Box>
                      <Text fontSize="sm" pl="1.5" overflow="hidden" textOverflow="ellipsis">
                        {contract.publishedByName}
                      </Text>
                    </Flex>
                  ) : (
                    <Text key="publishedBy" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  contract.noticePeriod ? (
                    <Text key="noticePeriod" fontSize="sm">
                      {contract.noticePeriod} {t("days")}
                    </Text>
                  ) : (
                    <Text key="noticePeriod" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  <Text key="documentCount" fontSize="sm">
                    {contract.documentIds?.length || 0}
                  </Text>,
                  <Text key="signedDocumentCount" fontSize="sm">
                    {contract.signedDocumentIds?.length || 0}
                  </Text>,
                  contract.terminationDate ? (
                    <Text key="terminationDate" fontSize="sm">
                      {displayDate(contract.terminationDate)}
                    </Text>
                  ) : (
                    <Text key="terminationDate" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not terminated")}
                    </Text>
                  ),
                  contract.terminatedById ? (
                    <Text key="terminatedBy" fontSize="sm" overflow="hidden" textOverflow="ellipsis">
                      {contract.terminatedByName}
                    </Text>
                  ) : (
                    <Text key="terminatedBy" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not terminated")}
                    </Text>
                  ),
                  contract.terminationReason ? (
                    <Tooltip key="terminationReason" label={contract.terminationReason}>
                      <Text fontSize="sm" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                        {contract.terminationReason}
                      </Text>
                    </Tooltip>
                  ) : (
                    <Text key="terminationReason" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not terminated")}
                    </Text>
                  ),
                  contract.counterpartyContactFullName ? (
                    <Flex key="counterpartyContactFullName" align="center">
                      <Box>
                        <ProfileImageRounded
                          name={contract.counterpartyContactFullName}
                          backgroundColor="smSecondary"
                          width="20px"
                          fontSize="xx-small"
                        />
                      </Box>
                      <Text fontSize="sm" pl="1.5" overflow="hidden" textOverflow="ellipsis">
                        {contract.counterpartyContactFullName}
                      </Text>
                    </Flex>
                  ) : (
                    <Text key="counterpartyContactFullName" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  contract.counterpartyContactEmail ? (
                    <Text key="counterpartyContactEmail" fontSize="sm" overflow="hidden" textOverflow="ellipsis">
                      {contract.counterpartyContactEmail}
                    </Text>
                  ) : (
                    <Text key="counterpartyContactEmail" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  contract.counterpartyContactPhoneNumber ? (
                    <Text key="counterpartyContactPhone" fontSize="sm" overflow="hidden" textOverflow="ellipsis">
                      {contract.counterpartyContactPhoneNumber}
                    </Text>
                  ) : (
                    <Text key="counterpartyContactPhone" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  contract.externalLink ? (
                    <Button
                      key="externalLink"
                      as="a"
                      href={contract.externalLink}
                      target="_blank"
                      variant="link"
                      colorScheme="teal"
                      size="xs"
                      fontSize="sm"
                    >
                      {t("Link")}
                    </Button>
                  ) : (
                    <Text key="externalLink" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("None")}
                    </Text>
                  ),
                  contract.linkedContracts?.length ? (
                    <Text key="linkedContracts" fontSize="sm">
                      {contract.linkedContracts.length} {t("linked")}
                    </Text>
                  ) : (
                    <Text key="linkedContracts" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("None")}
                    </Text>
                  ),
                  contract.paymentTermsInDays ? (
                    <Text key="paymentTerms" fontSize="sm">
                      {contract.paymentTermsInDays} {t("days")}
                    </Text>
                  ) : (
                    <Text key="paymentTerms" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  contract.pricePerMonth ? (
                    <Text key="pricePerMonth" fontSize="sm">
                      {displaySum({ value: contract.pricePerMonth, currency: contract.currency ?? "NOK" })}
                    </Text>
                  ) : (
                    <Text key="pricePerMonth" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  contract.pricePerYear ? (
                    <Text key="pricePerYear" fontSize="sm">
                      {displaySum({ value: contract.pricePerYear, currency: contract.currency ?? "NOK" })}
                    </Text>
                  ) : (
                    <Text key="pricePerYear" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  contract.annualValue != null ? (
                    <Text key="annualValue" fontSize="sm">
                      {displaySum({ value: contract.annualValue, currency: contract.currency ?? "NOK" })}
                    </Text>
                  ) : (
                    <Text key="annualValue" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  contract.purchasingPolicy ? (
                    <Tooltip key="purchasingPolicy" label={contract.purchasingPolicy}>
                      <Text fontSize="sm" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                        {contract.purchasingPolicy}
                      </Text>
                    </Tooltip>
                  ) : (
                    <Text key="purchasingPolicy" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  contract.supplierInfo ? (
                    <VStack key="supplierInfo" spacing="1" align="stretch">
                      <HStack spacing="1">
                        <Icon
                          as={contract.supplierInfo.isClimateCommitted ? CheckIcon : CloseIcon}
                          color={contract.supplierInfo.isClimateCommitted ? "green.500" : "red.500"}
                          boxSize="3"
                        />
                        <Text fontSize="xs">{t("Climate")}</Text>
                      </HStack>
                      <HStack spacing="1">
                        <Icon
                          as={contract.supplierInfo.hasSignedCodeOfConduct ? CheckIcon : CloseIcon}
                          color={contract.supplierInfo.hasSignedCodeOfConduct ? "green.500" : "red.500"}
                          boxSize="3"
                        />
                        <Text fontSize="xs">{t("Code of conduct")}</Text>
                      </HStack>
                      <HStack spacing="1">
                        <Icon
                          as={contract.supplierInfo.hasCustomerContact ? CheckIcon : CloseIcon}
                          color={contract.supplierInfo.hasCustomerContact ? "green.500" : "red.500"}
                          boxSize="3"
                        />
                        <Text fontSize="xs">{t("Contact")}</Text>
                      </HStack>
                    </VStack>
                  ) : (
                    <Text key="supplierInfo" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  contract.totalLiability ? (
                    <Text key="totalLiability" fontSize="sm">
                      {displaySum({ value: contract.totalLiability, currency: contract.currency ?? "NOK" })}
                    </Text>
                  ) : (
                    <Text key="totalLiability" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  contract.warrantyExpirationDate ? (
                    <Text key="warrantyExpiration" fontSize="sm">
                      {displayDate(contract.warrantyExpirationDate)}
                    </Text>
                  ) : (
                    <Text key="warrantyExpiration" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  contract.supplierCategory ? (
                    <Tag key="supplierCategory" size="sm" colorScheme="purple" variant="outline">
                      {contract.supplierCategory}
                    </Tag>
                  ) : (
                    <Text key="supplierCategory" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("Not provided")}
                    </Text>
                  ),
                  contract.options?.length ? (
                    <Text key="options" fontSize="sm">
                      {contract.options.length} {t("options")}
                    </Text>
                  ) : (
                    <Text key="options" fontSize="sm" fontStyle="italic" color="smMuted">
                      {t("None")}
                    </Text>
                  ),
                  contract.owningOrganizationId !== authState.selectedOrg.id ? (
                    <Explanation
                      key="owner"
                      text={t(`${contract.owningOrganizationName} shared this contract with you`)}
                      enabled={true}
                    >
                      <Text fontSize="sm" color="smSecondary">
                        {contract.owningOrganizationName}
                      </Text>
                    </Explanation>
                  ) : (
                    <Explanation key="owner" text={t("Your organization owns this contract")} enabled={true}>
                      <Text fontSize="sm" color="smPrimary">
                        {t("Your org")}
                      </Text>
                    </Explanation>
                  ),
                  ...customFields.map((field) => {
                    const fieldValue = getCustomFieldValue(contract, field.id);
                    if (!fieldValue) return null;
                    return (
                      <CustomContractFieldValue
                        key={field.id}
                        field={fieldValue}
                        onOpenRichTextPreview={(content) => setRichTextPreviewContent(content)}
                      />
                    );
                  }),
                  <Box key="actions">
                    <Button
                      leftIcon={<FaEye />}
                      variant="outline"
                      colorScheme="teal"
                      size="xs"
                      onClick={() => handleContractClick(contract)}
                    >
                      {t("View")}
                    </Button>
                  </Box>,
                  ];
                }) ?? []
              }
              isLoading={listIsLoading}
              count={data?.count}
              limit={filters.q.limit ?? 10}
              skip={filters.q.skip ?? 0}
              setSkip={(skip) => handleFilterChange({ q: { ...filters.q, skip } }, false)}
              onReset={handleReset}
              hasActiveFilters={hasActiveFilters}
            />
          </Box>
        </GridItem>
      </Grid>
    </>
  );
};

const openedColumnsAndSort = (search: string): { columns?: string[]; sortByCustomField?: string | null } => {
  const params = new URLSearchParams(search);
  const encodedColumns = params.get("columns");
  try {
    return {
      columns: encodedColumns ? (JSON.parse(base64Decode(encodedColumns)) as string[]) : undefined,
      sortByCustomField: params.get("sortByCustomField"),
    };
  } catch {
    return { sortByCustomField: params.get("sortByCustomField") };
  }
};
